import { useEffect, useRef, useState } from 'react';
import type { AppealSent } from '../types';
import { money } from '../utils';
import { hasMoneyPermission, MONEY_APPROVAL_NOTE } from '@/features/shared/permissions';

function Modal({ label, onCancel, children }: { label: string; onCancel: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, input, [tabindex]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);
  return (
    <div className="apl-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div ref={ref} className="apl-dialog" role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>
  );
}

// ---- Decide (appeals sent, §9) ----
export function DecideDialog({ row, onCancel, onConfirm }: { row: AppealSent; onCancel: () => void; onConfirm: () => void }) {
  const [choice, setChoice] = useState('');
  const amount = row.billAmount ?? row.billed;
  const title = row.decideTitle ?? `${row.payerShort} kept its denial — what now?`;
  // D245: Bill the patient and Write it off are hidden without the permission.
  const moneyOk = hasMoneyPermission();
  const options = [
    ...(row.hasAnotherLevel ? [{ id: 'again', label: `Appeal again${row.nextLevelDeadline ? ` · by ${row.nextLevelDeadline}` : ''}` }] : []),
    ...(moneyOk
      ? [
          { id: 'bill', label: `Bill ${row.patient} ${money(amount)}` },
          { id: 'write_off', label: 'Write it off' },
        ]
      : []),
  ];
  return (
    <Modal label={title} onCancel={onCancel}>
      <h2 className="apl-dialog-title">{title}</h2>
      <div className="apl-radio-list" role="radiogroup" aria-label="What now">
        {options.map((o) => (
          <label key={o.id} className="apl-radio">
            <input type="radio" name="apl-decide" checked={choice === o.id} onChange={() => { setChoice(o.id); }} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {!moneyOk ? <p className="apl-meta">{MONEY_APPROVAL_NOTE}</p> : null}
      <div className="apl-dialog-actions">
        <button type="button" className="apl-btn apl-btn-primary" disabled={choice === ''} onClick={onConfirm}>Confirm</button>
        <button type="button" className="apl-btn" onClick={onCancel}>Cancel</button>
      </div>
    </Modal>
  );
}
